import { Theme } from '@stadionhq-v3/core';
import {
  EmbedBlockProps,
  ScriptEmbed,
  SocialEmbed,
  UseClient,
} from '@stadionhq-v3/base-ui';
import {
  CleanContentful,
  CleanedContentfulImageProps,
} from '@stadionhq-v3/clean-contentful';
import {
  EmbedBlock,
  EmbedBlockItemToEmbed,
  Gallery,
  Image,
} from '@graphql/index';
import {
  EMBED_CONTENT_TYPES,
  TAG_CONTENT_TYPES,
} from '@core/constants/contentTypes.constants';
import videoEmbedTransform from '@utils/transforms/components/organisms/videoEmbed.transform';
import imageEmbedTransform from '@utils/transforms/components/organisms/imageEmbed.transform';
import pullQuoteEmbedTransform from '@utils/transforms/components/organisms/pullQuoteEmbed.transform';
import galleryEmbedTransform from '@utils/transforms/components/organisms/galleryEmbed.transform';
import socialEmbedTransform from '@utils/transforms/components/organisms/socialEmbed.transform';
import PullQuoteEmbed from '@components/v3/organisms/pull-quote-embed';
import GalleryEmbed from '@components/v3/organisms/gallery-embed';
import VideoEmbed from '@components/v3/organisms/video-embed';
import ImageEmbed from '@components/v3/organisms/image-embed';
import { RteDictionary } from '@components/helpers/contentful-rich-text-render';
import styles from '@components/v3/organisms/embed-block/embedBlock.module.scss';
import scriptEmbedTransform from './scriptEmbed.transform';

function transformEmbed(
  embed:
    | CleanContentful<Exclude<EmbedBlockItemToEmbed, Image>>
    | CleanedContentfulImageProps,
  theme: Theme,
  dictionary: RteDictionary,
  packagePageLinkPath?: string
) {
  switch (embed?.__typename) {
    case EMBED_CONTENT_TYPES.PULL_QUOTE_EMBED:
      return (
        <PullQuoteEmbed {...pullQuoteEmbedTransform(embed)} theme={theme} />
      );
    case TAG_CONTENT_TYPES.IMAGE_TAG:
      return <ImageEmbed {...imageEmbedTransform(embed)} theme={theme} />;
    case TAG_CONTENT_TYPES.VIDEO_TAG:
      return (
        <UseClient>
          <VideoEmbed
            {...videoEmbedTransform(embed)}
            dictionary={dictionary}
            theme={theme}
            packagePageLinkPath={packagePageLinkPath}
          />
        </UseClient>
      );
    case TAG_CONTENT_TYPES.GALLERY_TAG:
      return (
        <UseClient>
          <GalleryEmbed
            {...galleryEmbedTransform(
              embed as CleanContentful<Gallery> & {
                images?: CleanedContentfulImageProps[];
              },
              dictionary
            )}
            theme={theme}
          />
        </UseClient>
      );
    case EMBED_CONTENT_TYPES.SOCIAL_EMBED:
      return (
        <div className={styles.scriptEmbedWrapper}>
          <SocialEmbed {...socialEmbedTransform(embed)} />
        </div>
      );
    case EMBED_CONTENT_TYPES.SCRIPT_EMBED:
      return (
        <div className={styles.scriptEmbedWrapper}>
          <ScriptEmbed {...scriptEmbedTransform(embed)} />
        </div>
      );

    default:
      return <></>;
  }
}

const embedBlockTransform = (
  { itemToEmbed, theme }: CleanContentful<EmbedBlock>,
  dictionary: RteDictionary,
  packagePageLinkPath?: string
): EmbedBlockProps => ({
  theme: (theme as Theme) || undefined,
  embed:
    itemToEmbed &&
    transformEmbed(
      itemToEmbed,
      theme as Theme,
      dictionary,
      packagePageLinkPath
    ),
});

export default embedBlockTransform;
